import { useLayoutEffect, useState } from 'react';

import { SELECT_SKELETON_MENU_POSITIONS, SelectSkeletonMenuPosition } from '../SelectSkeleton.decl';

export interface MenuPositionOptions {
  /** Specified position of the menu. If provided, it will not detect the position automatically */
  menuPosition?: SelectSkeletonMenuPosition;

  /** Ref to the clickable select */
  containerRef: React.RefObject<HTMLDivElement>;

  /** Ref to the closest outer scrollable region */
  outerScrollableEle?: HTMLElement;

  /** Minimum distance required between the container and the bottom of the outer scrollable region */
  minBottomOffset: number;
}

/**
 * Computes the position of the menu based on the position of `<Select>` inside the outer scrollable container.
 * **Note**: It will not compute the position if `menuPosition` is specified.
 */
export const useMenuPosition = ({
  menuPosition,
  containerRef,
  outerScrollableEle,
  minBottomOffset,
}: MenuPositionOptions): SelectSkeletonMenuPosition => {
  /** Determines where the menu should be shown */
  const [computedMenuPos, setComputedMenuPos] = useState(
    menuPosition || SELECT_SKELETON_MENU_POSITIONS.BOTTOM
  );

  useLayoutEffect(() => {
    // should not compute the position of menu if it is specified
    if (menuPosition || !(containerRef && containerRef.current && outerScrollableEle)) {
      return;
    }

    const scrollableBottom =
      outerScrollableEle.getBoundingClientRect().top + outerScrollableEle.scrollHeight;

    const bottomOffset = scrollableBottom - containerRef.current.getBoundingClientRect().bottom;

    if (bottomOffset < minBottomOffset) {
      setComputedMenuPos(SELECT_SKELETON_MENU_POSITIONS.TOP);
    }
  }, [containerRef, menuPosition, outerScrollableEle, setComputedMenuPos, minBottomOffset]);

  return computedMenuPos;
};
